import { stripSemanticTypeScope } from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import type { SimulationDependency } from '../../simulation.types'
import { findTasksWhichDependOnStaleData } from './findTasksWhichDependOnStaleData'

/**
 * Which tasks we need to fetch again after something changes. It's the tasks
 * producing the changed values, and then everything reading those, and so on.
 *
 * The second part is easy to forget and hard to spot: `getCustomer` is fetched
 * with a customer id that came off `getQuote`, so once the quote is stale the
 * customer is stale too, even though nothing about the customer changed.
 *
 *   sequence.quote.currency -> getQuote -> getCustomer (reads quote.customerId)
 */
export const selectStaleDependencies = ({
  dependencies,
  semanticTypes
}: {
  dependencies: SimulationDependency[]
  semanticTypes: string[]
}): string[] => {
  // A supporting task tags its values with the subject they're about, like
  // 'sequence.quoteTemplate.attachments::tpl-a'. We compare without that tag, so
  // asking about the plain value still matches every template it was read for.
  const stale = semanticTypes.map(stripSemanticTypeScope)

  const tasksWithStaleOutputs = dependencies
    .filter(
      dependency =>
        publishesAStaleValue({ dependency, stale }) ||
        publishesNothingOfItsOwn(dependency)
    )
    .map(dependency => dependency.task.taskReferenceName)

  return tasksWithStaleOutputs.reduce<string[]>(
    (collected, taskReferenceName) =>
      findTasksWhichDependOnStaleData({
        collected,
        taskReferenceName,
        dependencies
      }),
    []
  )
}

const publishesAStaleValue = ({
  dependency,
  stale
}: {
  dependency: SimulationDependency
  stale: string[]
}): boolean =>
  dependency.semanticTypes.some(semanticType =>
    stale.includes(stripSemanticTypeScope(semanticType))
  )

/**
 * Semantic types are declared at the node boundary, so a task feeding another
 * task inside the same node publishes none of its own. With no names to match
 * against, nothing can tell us whether it has gone stale - so we always fetch
 * it again.
 *
 * This is the same call `staleTimeFor` makes on a first run, where a task like
 * this is never allowed to come from the cache either. It costs a request we may
 * not have needed. The alternative costs a prediction about the quote as it was
 * before the user's last edit, which is the one thing this is all for.
 */
const publishesNothingOfItsOwn = (dependency: SimulationDependency): boolean =>
  dependency.semanticTypes.length === 0
